import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useQuery } from "@apollo/client";

import Button from "@material-ui/core/Button";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";

import imgSleep from "~assets/images/visits/sleep-bottles.jpg";
import {
  DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
  DOCTOR_VISIT_STATUS_PENDING,
  DOCTOR_VISIT_STATUS_READY,
  DOCTOR_VISIT_STATUS_AWAITING_REPLY,
  VISIT_TYPE_INSOMNIA_FOLLOW_UP,
  DB_STATUS_ACTIVE,
  DB_STATUS_CANCELED,
} from "~/shared/sharedConstants";
import { displayFriendlyDate, isNullDate } from "~/shared/utils/sharedUtils";
import { setCookiesAndRedirectToInsomniaFollowUpVisit } from "~/visits/utils/visitUtils";
import {
  clearVisitCookies,
  setActiveSubscriptionBySubType,
  clearActiveSubscriptionsBySubType,
} from "~/visits/utils/visitCookies";
import { GET_PATIENT_RECORD } from "./portalQueries";
import { displayVisitStatus } from "~/emr/utils/displayUtils";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
}));

const subscriptionItemStyle = {
  backgroundColor: "white",
  padding: "20px",
  width: "100%",
  margin: "0 0 10px",
  borderRadius: "5px",
  boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.1)",
  maxWidth: "800px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

function prescriptionWithFillRequests(prescription, fillRequests) {
  let refillsRemaining = prescription.refills - prescription.numRefillsFilled;
  refillsRemaining = Math.min(refillsRemaining, prescription.refills);
  const rxWithFill = {
    ...prescription,
    fillRequests: fillRequests.filter(
      (fillRequest) =>
        fillRequest.doctorPrescription1ID === prescription.id ||
        fillRequest.doctorPrescription2ID === prescription.id ||
        fillRequest.doctorPrescription3ID === prescription.id ||
        fillRequest.doctorPrescription4ID === prescription.id
    ),
    refillsRemaining,
  };
  rxWithFill.mostRecentFillRequest = rxWithFill.fillRequests.reduce(
    (mostRecent, fillRequest) =>
      mostRecent.createdAt > fillRequest.createdAt ? mostRecent : fillRequest,
    { createdAt: 0 }
  );

  return rxWithFill;
}

function hasFirstPendingSubVisit(data) {
  if (!data?.patientRecord?.visits?.length) {
    return false;
  }
  // sort visits by visitDate having the most recent visit first
  const sortedVisits = [...data.patientRecord.visits].sort(
    (a, b) => new Date(b.visitDate) - new Date(a.visitDate)
  );
  for (let visit of sortedVisits) {
    if (
      visit.visitType === "IN" &&
      !displayVisitStatus(visit.status).includes("Closed")
    ) {
      return true;
    }
  }
}

function hasPendingFollowUpVisit(latestVisit) {
  return (
    latestVisit.visitType === VISIT_TYPE_INSOMNIA_FOLLOW_UP &&
    [
      DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
      DOCTOR_VISIT_STATUS_PENDING,
      DOCTOR_VISIT_STATUS_READY,
      DOCTOR_VISIT_STATUS_AWAITING_REPLY,
    ].includes(latestVisit.status)
  );
}

function RenderAutoshipMed({ rxWithFill }) {
  return (
    <div>
      <h4>{`${rxWithFill.medicationName} ${rxWithFill.medicationDosage}`}</h4>
      <h5
        style={{
          fontSize: "15px",
          marginBottom: "4px",
          marginTop: "4px",
        }}
      >
        {rxWithFill.quantity} {rxWithFill.medicationFormFactor}s
      </h5>
      <h5
        style={{
          fontSize: "15px",
          marginBottom: "4px",
          marginTop: "4px",
        }}
      >
        {isNullDate(rxWithFill.mostRecentFillRequest?.completedAt)
          ? "Pending shipment"
          : `Last sent ${displayFriendlyDate(
              rxWithFill.mostRecentFillRequest?.completedAt
            )}`}
      </h5>
      {!!rxWithFill.refillsRemaining && (
        <>
          <h5
            style={{
              fontSize: "15px",
              marginBottom: "4px",
              marginTop: "4px",
            }}
          >
            {rxWithFill.refillsRemaining} refill
            {rxWithFill.refillsRemaining === 1 ? "" : "s"} remaining
          </h5>
          {!isNullDate(rxWithFill.autoshipNextShipDate) && (
            <h5
              style={{
                fontSize: "15px",
                marginBottom: "4px",
                marginTop: "4px",
              }}
            >
              Next shipment{" "}
              {displayFriendlyDate(rxWithFill.autoshipNextShipDate)}
            </h5>
          )}
        </>
      )}
      {!rxWithFill.refillsRemaining && (
        <div
          style={{
            fontSize: "15px",
            marginBottom: "4px",
            marginTop: "16px",
            paddingRight: "20px",
            // textAlign: "center",
          }}
        >
          No refills remaining. Please complete a follow-up visit for a new
          prescription.
        </div>
      )}
    </div>
  );
}

function rxStatus(rxWithFill, orderType) {
  if (orderType.includes("visit-only")) {
    return "";
  }
  return isNullDate(rxWithFill.mostRecentFillRequest?.completedAt)
    ? "Pending shipment"
    : `Sent ${displayFriendlyDate(
        rxWithFill.mostRecentFillRequest?.completedAt
      )}`;
}

function RenderNonAutoshipMed({ rxWithFill, idx, latestVisit }) {
  return (
    <div
      style={{
        padding: "10px 4px",
        borderTop: idx ? "1px solid #eee" : "none",
      }}
    >
      <h4
        style={{ margin: "0", fontSize: "17px" }}
      >{`${rxWithFill.medicationName} ${rxWithFill.medicationDosage}`}</h4>
      <h5
        style={{
          fontSize: "15px",
          marginBottom: "3px",
          marginTop: "3px",
        }}
      >
        {rxWithFill.quantity} {rxWithFill.medicationFormFactor}s
      </h5>
      <h5
        style={{
          fontSize: "15px",
          marginBottom: "0px",
          marginTop: "0px",
        }}
      >
        {rxStatus(rxWithFill, latestVisit.orderType)}
      </h5>
    </div>
  );
}

const PortalMySubscriptions = () => {
  const classes = useStyles();
  const { data } = useQuery(GET_PATIENT_RECORD);
  const subscriptionsBySubType = {};

  clearActiveSubscriptionsBySubType();

  // create a map of subscriptions grouped by subType
  (data?.patientRecord?.subscriptions || []).forEach((subscription) => {
    // Only render canceled sub if no active sub exists
    if (
      !subscriptionsBySubType[subscription.subType] ||
      subscription.status === DB_STATUS_ACTIVE
    ) {
      subscriptionsBySubType[subscription.subType] = subscription;
      if (subscription.status === DB_STATUS_ACTIVE) {
        setActiveSubscriptionBySubType(subscription.subType, subscription);
      }
    }
  });

  return (
    <main className={classes.content}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <h3 style={{ textAlign: "center", marginBottom: "20px" }}>
          My Subscriptions
        </h3>
        {Object.values(subscriptionsBySubType).map((subscription) => {
          if (!subscription.prescriptions) {
            return null;
          }
          const hasRefillsRemaining = subscription.prescriptions.some(
            (rx) => rx.numRefillsFilled < rx.refills
          );
          const hasAutoship = subscription.prescriptions.some(
            (rx) => rx.autoshipStatus === DB_STATUS_ACTIVE
          );
          const hasPrescriptions = subscription.prescriptions.length > 0;

          return (
            <div
              key={subscription.id}
              style={{
                backgroundColor: "white",
                padding: "20px 20px 0",
                width: "100%",
                margin: "0 0 10px",
                borderRadius: "5px",
                boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.1)",
                maxWidth: "800px",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  display: "flex",
                  marginBottom: "10px",
                  alignItems: "center",
                  width: "100%",
                  borderBottom: "1px solid #eee",
                  paddingBottom: "16px",
                }}
              >
                <img
                  style={{
                    width: "48px",
                    height: "50px",
                    position: "relative",
                    marginRight: "14px",
                    objectFit: "cover",
                  }}
                  src={imgSleep}
                ></img>
                <h3
                  style={{
                    fontSize: "23px",
                    margin: "0",
                  }}
                >
                  Sleep Membership
                  {subscription.status === DB_STATUS_CANCELED && (
                    <div style={{ fontSize: "14px" }}>
                      Canceled.{" "}
                      {hasPrescriptions &&
                        "Complete a follow-up to restart your subscription."}
                    </div>
                  )}
                  {subscription.status !== DB_STATUS_CANCELED &&
                    (!isNullDate(subscription.cancelAt) ||
                      !isNullDate(subscription.canceledAt)) && (
                      <span style={{
                        color: "white",
                        backgroundColor: "#e53e3e",
                        fontSize: "13px",
                        fontWeight: "600",
                        marginLeft: "12px",
                        padding: "2px 10px",
                        borderRadius: "4px",
                        verticalAlign: "middle",
                      }}>
                        Canceled
                      </span>
                    )}
                </h3>
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  width: "100%",
                  justifyContent: "space-around",
                  alignItems: "center",
                }}
              >
                {!hasPrescriptions && (
                  <div style={{ margin: "20px", fontSize: "16px" }}>
                    Your visit will be reviewed by one of our doctors soon.
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    margin: "0 20px 15px",
                    flex: "1 1 0px",
                  }}
                >
                  <div>
                    {subscription.prescriptions.map((prescription, idx) => {
                      const rxWithFill = prescriptionWithFillRequests(
                        prescription,
                        subscription.fillRequests
                      );
                      return rxWithFill.autoshipStatus === DB_STATUS_ACTIVE ? (
                        <RenderAutoshipMed
                          key={rxWithFill.id}
                          rxWithFill={rxWithFill}
                        />
                      ) : (
                        <RenderNonAutoshipMed
                          key={rxWithFill.id}
                          rxWithFill={rxWithFill}
                          idx={idx}
                          latestVisit={subscription.latestVisit}
                        />
                      );
                    })}
                  </div>
                </div>
                {hasPrescriptions && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      flex: "1 1 0px",
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "360px",
                        textAlign: "center",
                        marginBottom: "10px",
                        whiteSpace: "pre-line",
                      }}
                    >
                      {subscription.status === DB_STATUS_CANCELED ? (
                        "Your subscription has been canceled. Complete a follow-up visit to restart your subscription."
                      ) : hasPendingFollowUpVisit(subscription.latestVisit) ? (
                        "Your follow-up visit is currently under review by one of our doctors."
                      ) : (
                        <>
                          {hasRefillsRemaining &&
                            hasPrescriptions &&
                            (hasAutoship
                              ? "Want to update something or try a new medication? Your subscription allows for unlimited follow-up visits."
                              : "How is your medication trial going?\nComplete a follow-up visit any time to adjust medication or set up automatic refill shipments.")}
                        </>
                      )}
                    </div>
                    <div>
                      <Button
                        color="primary"
                        size="large"
                        style={{
                          backgroundColor: "#3f3f82",
                          color: "white",
                          width: "300px",
                          marginBottom: "20px",
                        }}
                        onClick={async () => {
                          // TODO: dont hardcode to insomnia
                          setCookiesAndRedirectToInsomniaFollowUpVisit(
                            data.patientRecord.patient,
                            subscription.latestVisit
                          );
                        }}
                      >
                        Follow-up Visit&nbsp;
                        <NavigateNextIcon />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {hasFirstPendingSubVisit(data) && (
          <div
            style={{
              ...subscriptionItemStyle,
            }}
          >
            <h5>
              Thank you for completing an insomnia visit. It is currently under
              review by one our doctors.
            </h5>
          </div>
        )}

        {data &&
          !data.patientRecord?.subscriptions?.length &&
          !hasFirstPendingSubVisit(data) && (
            <div
              style={{
                ...subscriptionItemStyle,
              }}
            >
              <h5>No subscriptions at the moment</h5>
              <Button
                onClick={() => {
                  clearVisitCookies();
                  window.location.href = `/visits/inc/1`;
                }}
                color="primary"
                size="large"
                style={{
                  backgroundColor: "#3f3f82",
                  color: "white",
                  width: "300px",
                  margin: "20px 0",
                }}
              >
                Start Insomnia Visit&nbsp;
                <NavigateNextIcon />
              </Button>
            </div>
          )}
      </div>
    </main>
  );
};

export default PortalMySubscriptions;
